import { Component, Input, OnChanges, OnInit, OnDestroy, Output, EventEmitter, SimpleChanges, ViewChild } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { Subject, of } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, map, switchMap, takeUntil } from 'rxjs/operators';
import { LazyScrollableDataSource } from '@rappi/common';
import { SelectComponent } from '@rappi/ui/select';
import { AlertsType } from '@rappi/ui/alerts';

import { MakersFinancesMgrService } from '../../../../services/makers-finances-mgr.service';
import { FiltersService } from '../../../../../permissions/services/filters.service';
import { StoreAuxService } from '../../../../../services/storeAux/store-aux.service';
import { AlertsService } from '../../../../../services/alerts/alerts.service';
import { HasPermissionsService } from '../../../../../shared/services/has-permissions/has-permissions.service';
import { CatalogItem, NewPurchaseOrderConfig } from '../../../../models/makers-finances-mgr.models';
import { environment } from '../../../../../../environments/environment';
import { DETAIL_MODE, DetailMode, SourceType, PO_FIELD, PO_FIELD_LABELS, PO_SECTION_LABELS, PO_SECTION, PO_OWNER_ERROR } from '../../purchase-order.constants';
import { SelectOption, staticSourceFromCatalog } from '../section-base.types';

@Component({
  selector: 'app-section-general-info',
  templateUrl: './section-general-info.component.html',
  styleUrls: ['./section-general-info.component.scss'],
})
export class SectionGeneralInfoComponent implements OnInit, OnChanges, OnDestroy {
  /* istanbul ignore next -- decorator metadata */
  @Input() form: FormGroup;
  /* istanbul ignore next -- decorator metadata */
  @Input() mode: DetailMode;
  /* istanbul ignore next -- decorator metadata */
  @Input() sourceType: SourceType;
  /* istanbul ignore next -- decorator metadata */
  @Input() remoteConfig: NewPurchaseOrderConfig | null;
  /* istanbul ignore next -- decorator metadata */
  @Input() sourceTypeCatalog: CatalogItem[] = [];
  /* istanbul ignore next -- decorator metadata */
  @Input() poTypeCatalog: CatalogItem[] = [];
  /* istanbul ignore next -- decorator metadata */
  @Input() catalogsLoaded = false;
  /* istanbul ignore next -- decorator metadata */
  @Input() submitted = false;
  /* istanbul ignore next -- decorator metadata */
  @Input() fieldConfigs: Record<string, any> = {};
  /* istanbul ignore next -- decorator metadata */
  @Input() lockedSourceType: SourceType | null = null;
  // Flips true once the parent has patched the form from the loaded PO; triggers setSelection on
  // the selects (one-select is not form-bound).
  /* istanbul ignore next -- decorator metadata */
  @Input() poLoaded = false;

  // In edit mode the skeleton stays until the PO detail is also loaded (poLoaded), not just the
  // catalogs. In create mode only catalogsLoaded matters.
  get showSkeleton(): boolean {
    return !this.catalogsLoaded || (this.mode === DETAIL_MODE.EDIT && !this.poLoaded);
  }

  readonly F = PO_FIELD;
  readonly LABELS = PO_FIELD_LABELS;
  readonly sectionLabel = PO_SECTION_LABELS[PO_SECTION.GENERAL_INFORMATION];

  readonly selectConfig = { search: false, includeAll: false, showTotalsChip: false, maxNumberOfChips: 1, panelClass: '' };
  readonly searchSelectConfig = { search: true, includeAll: false, showTotalsChip: false, maxNumberOfChips: 1, panelClass: '' };
  readonly makerSelectConfig = { search: true, includeAll: false, showTotalsChip: false, maxNumberOfChips: 1, panelClass: '' };

  /* istanbul ignore next -- decorator metadata */
  @ViewChild('ownerSelect') ownerSelect: SelectComponent<SelectOption>;
  /* istanbul ignore next -- decorator metadata */
  @ViewChild('sourceTypeSelect') sourceTypeSelect: SelectComponent<SelectOption>;
  /* istanbul ignore next -- decorator metadata */
  @ViewChild('makerSelect') makerSelect: SelectComponent<SelectOption>;
  /* istanbul ignore next -- decorator metadata */
  @ViewChild('poTypeSelect') poTypeSelect: SelectComponent<SelectOption>;

  selectSources: Record<string, LazyScrollableDataSource<SelectOption>> = {};
  private userEmail = '';

  // Owner validation state — drives the red banner and red border on the owner field.
  ownerInvalid = false;
  ownerValidating = false;
  ownerBannerMessage: string | null = null;

  @Output() ownerValidChange = new EventEmitter<boolean>();

  private readonly destroy$ = new Subject<void>();

  /* istanbul ignore next -- decorator metadata */
  constructor(
    private readonly financesService: MakersFinancesMgrService,
    private readonly filtersService: FiltersService,
    private readonly storeAux: StoreAuxService,
    private readonly alerts: AlertsService,
    private readonly permissionsService: HasPermissionsService,
  ) {}

  ngOnInit(): void {
    this.userEmail = localStorage.getItem('email') || '';

    this.selectSources[PO_FIELD.MAKER_TO_EXECUTE] = new LazyScrollableDataSource<SelectOption>(
      (page, limit, search) =>
        this.filtersService.getEntities(
          this.filtersService.getBody(environment.modules.financialHub, 'maker'),
          this.filtersService.getParams(page + 1, limit, search)
        ).pipe(map(res => res.data.records.map(e => ({ id: String(e.reference_code || e.id), name: e.name }))))
    );

    this.selectSources[PO_FIELD.OWNER] = new LazyScrollableDataSource<SelectOption>(
      (page, limit, search) =>
        this.financesService.searchOwners(this.storeAux.country, page + 1, limit, search || undefined)
          .pipe(map(res => res.data.records.map(o => ({ id: o.email, name: o.email }))))
    );

    this.form.get(PO_FIELD.OWNER)?.valueChanges.pipe(
      debounceTime(600),
      distinctUntilChanged(),
      takeUntil(this.destroy$),
    ).subscribe(value => this._validateOwner(value));
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private _validateOwner(email: string | null): void {
    if (!email) {
      this.ownerInvalid = false;
      this.ownerValidating = false;
      this.ownerBannerMessage = null;
      this.ownerValidChange.emit(true);
      return;
    }
    this.ownerValidating = true;
    this.financesService.validateOwner(email).pipe(
      catchError(() => of(null)),
      takeUntil(this.destroy$),
    ).subscribe(res => {
      this.ownerValidating = false;
      if (res?.data && !res.data.valid) {
        this.ownerInvalid = true;
        const template = this.remoteConfig?.rappi_owner?.banner?.missing_in_zoho ?? PO_OWNER_ERROR.BANNER;
        this.ownerBannerMessage = template.replace('{{owner_email}}', email);
        this.alerts.openAlerts(this.ownerBannerMessage, AlertsType.error);
        this.ownerValidChange.emit(false);
      } else {
        this.ownerInvalid = false;
        this.ownerBannerMessage = null;
        this.ownerValidChange.emit(true);
      }
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['catalogsLoaded'] && this.catalogsLoaded) {
      setTimeout(() => {
        if (this.mode === DETAIL_MODE.CREATE && this.userEmail && this.ownerSelect) {
          this.ownerSelect.setSelection({ id: this.userEmail, name: this.userEmail });
          this.form.get(PO_FIELD.OWNER)?.setValue(this.userEmail);
        }
      });
    }

    if (changes['sourceTypeCatalog'] && this.sourceTypeCatalog.length) {
      this.selectSources[PO_FIELD.SOURCE_TYPE] = staticSourceFromCatalog(this.sourceTypeCatalog);
      if (this.lockedSourceType) {
        const locked = this.sourceTypeCatalog.find(r => r.code === this.lockedSourceType);
        if (locked) {
          setTimeout(() => {
            this.sourceTypeSelect?.setSelection({ id: locked.code, name: locked.name });
          });
        }
      }
    }

    if (changes['poTypeCatalog'] && this.poTypeCatalog.length) {
      this.selectSources[PO_FIELD.PO_TYPE] = staticSourceFromCatalog(this.poTypeCatalog);
    }

    if (changes['sourceType'] && !changes['sourceType'].firstChange
        && this.isPoTypeVisible && this.poTypeCatalog.length) {
      this.selectSources[PO_FIELD.PO_TYPE] = staticSourceFromCatalog(this.poTypeCatalog);
    }

    if (changes['poLoaded'] && this.poLoaded) {
      setTimeout(() => {
        this._prefillSelects();
        this._validateOwner(this.form.get(PO_FIELD.OWNER)?.value);
      });
    }
  }

  // Edit mode: sync the select UIs to the patched form values. The form already holds the codes;
  // the selects just need their display set (one-select is not form-bound).
  private _prefillSelects(): void {
    const sourceCode = this.form.get(PO_FIELD.SOURCE_TYPE)?.value;
    if (sourceCode && this.sourceTypeSelect) {
      const item = this.sourceTypeCatalog.find(r => r.code === sourceCode);
      if (item) {
        this.sourceTypeSelect.setSelection({ id: item.code, name: item.name });
      }
    }

    const makerCode = this.form.get(PO_FIELD.MAKER_TO_EXECUTE)?.value;
    const makerName = this.form.get(PO_FIELD.MAKER_NAME)?.value;
    if (makerCode && this.makerSelect) {
      this.makerSelect.setSelection({ id: makerCode, name: makerName || makerCode });
    }

    const poTypeCode = this.form.get(PO_FIELD.PO_TYPE)?.value;
    if (poTypeCode && this.poTypeSelect) {
      const item = this.poTypeCatalog.find(r => r.code === poTypeCode);
      if (item) {
        this.poTypeSelect.setSelection({ id: item.code, name: item.name });
      }
    }

    const ownerEmail = this.form.get(PO_FIELD.OWNER)?.value;
    if (ownerEmail && this.ownerSelect) {
      this.ownerSelect.setSelection({ id: ownerEmail, name: ownerEmail });
    }
  }

  get isPoTypeVisible(): boolean {
    const cfg = this.fieldConfigs[PO_FIELD.PO_TYPE];
    if (!cfg) {
      return false;
    }
    if (cfg.visible === false) {
      return false;
    }
    if (cfg.visible_by_source) {
      return !!cfg.visible_by_source[this.sourceType];
    }
    return true;
  }

  get isPoTypeDisabled(): boolean {
    return this.mode === DETAIL_MODE.EDIT
      && this.fieldConfigs[PO_FIELD.PO_TYPE]?.editable_by_source?.[this.sourceType] === false;
  }

  get sourceTypeSelectDisabled(): boolean {
    if (this.lockedSourceType) {
      return true;
    }
    if (this.mode === DETAIL_MODE.EDIT
        && this.fieldConfigs[PO_FIELD.SOURCE_TYPE]?.editable_by_source?.[this.sourceType] === false) {
      return true;
    }
    return false;
  }

  get isParentPoVisible(): boolean {
    return this.mode === DETAIL_MODE.EDIT
      && !!this.fieldConfigs[PO_FIELD.PARENT_PO]?.visible_by_source?.[this.sourceType];
  }

  get isParentPoDisabled(): boolean {
    const permission = this.fieldConfigs[PO_FIELD.PARENT_PO]?.editable_by_permission;
    if (!permission) {
      return false;
    }
    return !this.permissionsService.checkPermission(permission);
  }

  onSelectChange(fieldKey: string, value: SelectOption): void {
    this.form.get(fieldKey)?.setValue(value?.id ?? null);
  }

  onMakerChange(value: SelectOption): void {
    this.form.get(PO_FIELD.MAKER_TO_EXECUTE)?.setValue(value?.id ?? null);
    this.form.get(PO_FIELD.MAKER_NAME)?.setValue(value?.name ?? null);
  }

  isInvalid(key: string): boolean {
    if (!this.submitted) {
      return false;
    }
    const ctrl = this.form.get(key);
    return !ctrl?.value || !!ctrl.errors?.['required'];
  }
}
